import type { EChartsOption } from "echarts";
import type {
  ClosureRateData,
  CompletionsData,
  DueByTypeData,
  DueBySizeData,
  LoadBreakdownData,
  LoadSizeMixData,
  OverdueByWeekData,
  TypeMixData,
  UnblockedOverdueData,
  WeeklyActivityData
} from "@silph-scope/shared";

// ECharts option builders ported from design_handoff_silph_scope/Silph Scope.dc.html's
// logic class — the design's colors, radii, and tooltip styling are final, so this
// keeps them verbatim rather than reinventing the chart language.

export type ChartTheme = "dark" | "light";

const PALETTE = {
  blue: "#335CFF",
  green: "#1FC16B",
  orange: "#FA7319",
  red: "#FB3748",
  yellow: "#F6B51E",
  purple: "#7D52F4",
  sky: "#47C2FF",
  pink: "#FB4BA3",
  teal: "#22D3BB"
};
const SERIES_COLOR_CYCLE = Object.values(PALETTE);
const CHART_FONT = '"Inter Variable", Inter, system-ui, sans-serif';

interface ThemeTokens {
  grid: string;
  axisLine: string;
  label: string;
  text: string;
  tipBg: string;
  tipStroke: string;
  legend: string;
  line: string;
  lineFill: string;
  trendFill: string;
  surface: string;
}

const THEMES: Record<ChartTheme, ThemeTokens> = {
  dark: {
    grid: "#252A36",
    axisLine: "#2B303B",
    label: "#99A0AE",
    text: "#F5F7FA",
    tipBg: "#222530",
    tipStroke: "#2B303B",
    legend: "#CACFD8",
    line: "#6E8DFF",
    lineFill: "rgba(110,141,255,.26)",
    trendFill: "rgba(110,141,255,.10)",
    surface: "#181B25"
  },
  light: {
    grid: "#F0F0F0",
    axisLine: "#EBEBEB",
    label: "#8C8C8C",
    text: "#171717",
    tipBg: "#FFFFFF",
    tipStroke: "#EBEBEB",
    legend: "#525252",
    line: "#335CFF",
    lineFill: "rgba(51,92,255,.22)",
    trendFill: "rgba(51,92,255,.07)",
    surface: "#FFFFFF"
  }
};

function px(n: number, scale: number): number {
  return Math.round(n * scale);
}

function base(theme: ChartTheme, scale = 1): EChartsOption {
  const T = THEMES[theme];
  return { textStyle: { fontFamily: CHART_FONT, color: T.legend, fontSize: px(12, scale) }, animationDuration: 500 };
}
function axisX(theme: ChartTheme, scale = 1) {
  const T = THEMES[theme];
  return { axisLine: { lineStyle: { color: T.axisLine } }, axisTick: { show: false }, axisLabel: { color: T.label, fontSize: px(11, scale) } };
}
function axisY(theme: ChartTheme, scale = 1) {
  const T = THEMES[theme];
  return { splitLine: { lineStyle: { color: T.grid } }, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: T.label, fontSize: px(11, scale) } };
}
function tip(theme: ChartTheme, scale = 1) {
  const T = THEMES[theme];
  return {
    backgroundColor: T.tipBg,
    borderColor: T.tipStroke,
    borderWidth: 1,
    textStyle: { color: T.text, fontSize: px(12, scale), fontFamily: CHART_FONT },
    extraCssText: "box-shadow:0 16px 32px -12px rgba(0,0,0,.35);border-radius:10px"
  };
}
function legendCfg(theme: ChartTheme, extra?: Record<string, unknown>, scale = 1) {
  const T = THEMES[theme];
  return Object.assign({ icon: "roundRect", itemWidth: px(8, scale), itemHeight: px(8, scale), itemGap: px(14, scale), textStyle: { color: T.legend, fontSize: px(11, scale) } }, extra ?? {});
}
function colorFor(name: string, index: number): string {
  return SERIES_COLOR_CYCLE[index % SERIES_COLOR_CYCLE.length]!;
}

/** Keep At risk on red; move 1-3 months off that slot so the two never match. */
function overdueByWeekColor(name: string, index: number): string {
  if (name === "At risk") return PALETTE.red;
  if (name === "1 – 3 months") return PALETTE.teal;
  return colorFor(name, index);
}

export function weeklyActivityOption(theme: ChartTheme, data: WeeklyActivityData, scale = 1): EChartsOption {
  const bar = (name: string, color: string, values: number[]) => ({
    name,
    type: "bar" as const,
    data: values,
    itemStyle: { color, borderRadius: [3, 3, 0, 0] },
    barMaxWidth: 16
  });
  return {
    ...base(theme, scale),
    grid: { left: px(34, scale), right: 8, top: px(34, scale), bottom: px(26, scale) },
    legend: legendCfg(theme, { top: 0, left: 0 }, scale),
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, ...tip(theme, scale) },
    xAxis: { type: "category", data: data.weeks, ...axisX(theme, scale) },
    yAxis: { type: "value", ...axisY(theme, scale) },
    series: [
      bar("Created", PALETTE.blue, data.created),
      bar("Due", PALETTE.purple, data.due),
      bar("Completed", PALETTE.green, data.completed)
    ]
  };
}

export function completionsOption(theme: ChartTheme, data: CompletionsData, scale = 1): EChartsOption {
  const T = THEMES[theme];
  const area = (color: string) => ({ areaStyle: { color } });
  const line = (name: string, color: string, values: number[], fillColor: string) => ({
    name,
    type: "line" as const,
    smooth: true,
    symbol: "none",
    data: values,
    lineStyle: { width: 2, color },
    ...area(fillColor)
  });
  return {
    ...base(theme, scale),
    grid: { left: px(30, scale), right: 10, top: px(34, scale), bottom: px(26, scale) },
    legend: legendCfg(theme, { top: 0, left: 0 }, scale),
    tooltip: { trigger: "axis", ...tip(theme, scale) },
    xAxis: { type: "category", data: data.days, boundaryGap: false, ...axisX(theme, scale) },
    yAxis: { type: "value", ...axisY(theme, scale) },
    series: [
      line("Early", PALETTE.teal, data.early, "rgba(34,211,187,.16)"),
      line("On time", T.line, data.onTime, T.lineFill),
      line("Late", PALETTE.orange, data.late, "rgba(250,115,25,.20)")
    ]
  };
}

export function dueByTypeOption(
  theme: ChartTheme,
  data: DueByTypeData,
  scale = 1,
  size?: { width: number; height: number }
): EChartsOption {
  const T = THEMES[theme];
  const total = data.types.reduce((s, t) => s + t.value, 0);
  const arrow = data.deltaPct > 0 ? "▲" : data.deltaPct < 0 ? "▼" : "";
  const deltaColor = data.deltaPct > 0 ? PALETTE.green : data.deltaPct < 0 ? PALETTE.red : T.label;
  const lastMonth = data.comparisonLabel.match(/vs\.\s*(\d+)/)?.[1] ?? "";

  const w = size?.width ?? 400;
  const h = size?.height ?? 274;
  const names = data.types.map((t) => t.name);
  const mid = Math.ceil(names.length / 2);
  const leftNames = names.slice(0, mid);
  const rightNames = names.slice(mid);
  const pad = px(6, scale);
  const gap = px(8, scale);
  const minDonut = px(96, scale);
  const sideBudget = Math.max(px(72, scale), (w - minDonut - gap * 2) / 2);
  const side = Math.min(Math.max(px(96, scale), w * 0.26), sideBudget);
  const box = Math.max(minDonut, Math.min(h - pad * 2, w - side * 2 - gap * 2));
  const outer = box * 0.46;
  const inner = box * 0.3;
  const hole = inner * 2;
  const totalSize = Math.max(16, Math.round(Math.min(px(28, scale), hole * 0.36)));
  const metaSize = Math.max(9, Math.round(Math.min(px(11, scale), hole * 0.11)));
  const labelWidth = Math.max(px(56, scale), side - px(22, scale));
  const legendCol = (dataNames: string[], sideKey: "left" | "right") =>
    legendCfg(
      theme,
      {
        type: dataNames.length > 10 ? "scroll" : "plain",
        orient: "vertical",
        [sideKey]: pad,
        top: "middle",
        align: "left",
        width: side,
        data: dataNames,
        itemGap: px(7, scale),
        pageIconSize: px(10, scale),
        pageTextStyle: { color: T.legend, fontSize: px(10, scale) },
        textStyle: {
          color: T.legend,
          fontSize: px(11, scale),
          width: labelWidth,
          overflow: "truncate"
        }
      },
      scale
    );

  return {
    ...base(theme, scale),
    tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)", ...tip(theme, scale) },
    legend: [
      ...(leftNames.length ? [legendCol(leftNames, "left")] : []),
      ...(rightNames.length ? [legendCol(rightNames, "right")] : [])
    ],
    series: [
      {
        type: "pie",
        radius: [inner, outer],
        center: [w / 2, h / 2],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: T.surface, borderWidth: 2 },
        label: {
          show: true,
          position: "center",
          fontFamily: CHART_FONT,
          formatter: `{a|${total}}\n{c|${arrow} ${Math.abs(data.deltaPct)}%}\n{b|vs. ${lastMonth}}\n{b|last month}`,
          rich: {
            a: {
              fontSize: totalSize,
              fontWeight: 500,
              color: T.text,
              fontFamily: CHART_FONT,
              lineHeight: Math.round(totalSize * 1.15)
            },
            b: { fontSize: metaSize, color: T.label, fontFamily: CHART_FONT, lineHeight: Math.round(metaSize * 1.35) },
            c: {
              fontSize: metaSize,
              fontWeight: 500,
              color: deltaColor,
              fontFamily: CHART_FONT,
              lineHeight: Math.round(metaSize * 1.45)
            }
          }
        },
        emphasis: { label: { show: true }, scaleSize: 4 },
        labelLine: { show: false },
        data: data.types.map((t, i) => ({ name: t.name, value: t.value, itemStyle: { color: colorFor(t.name, i) } }))
      }
    ]
  };
}

export function dueBySizeOption(theme: ChartTheme, data: DueBySizeData, scale = 1): EChartsOption {
  const T = THEMES[theme];
  return {
    ...base(theme, scale),
    grid: { left: px(82, scale), right: 14, top: px(30, scale), bottom: px(22, scale) },
    legend: legendCfg(theme, { top: 0, left: 0, itemGap: px(10, scale), textStyle: { color: T.legend, fontSize: px(10, scale) } }, scale),
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, ...tip(theme, scale) },
    xAxis: { type: "value", ...axisY(theme, scale) },
    yAxis: { type: "category", data: data.sizes, ...axisX(theme, scale), axisLabel: { color: T.label, fontSize: px(10, scale) } },
    series: data.series.map((s, i) => ({
      name: s.name,
      type: "bar" as const,
      stack: "s",
      data: s.values,
      barMaxWidth: 16,
      itemStyle: { color: colorFor(s.name, i) }
    }))
  };
}

export function unblockedOverdueOption(theme: ChartTheme, data: UnblockedOverdueData, scale = 1): EChartsOption {
  const T = THEMES[theme];
  const rows = [...data.rows].reverse();
  const max = Math.max(1, ...data.rows.map((r) => r.value));
  return {
    ...base(theme, scale),
    grid: { left: px(92, scale), right: 20, top: px(16, scale), bottom: px(22, scale) },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, ...tip(theme, scale) },
    xAxis: { type: "value", max, ...axisY(theme, scale) },
    yAxis: { type: "category", data: rows.map((r) => r.label), ...axisX(theme, scale), axisLabel: { color: T.label, fontSize: px(10, scale) } },
    series: [
      {
        type: "bar",
        data: rows.map((r, i) => ({ value: r.value, itemStyle: { color: colorFor(r.label, i), borderRadius: [0, 4, 4, 0] } })),
        barMaxWidth: 18,
        label: { show: true, position: "right", color: T.label, fontSize: px(11, scale) }
      }
    ]
  };
}

export function overdueByWeekOption(theme: ChartTheme, data: OverdueByWeekData, scale = 1): EChartsOption {
  const T = THEMES[theme];
  const last = data.weeks.length - 1;
  return {
    ...base(theme, scale),
    grid: { left: px(32, scale), right: 10, top: px(34, scale), bottom: px(26, scale) },
    legend: legendCfg(theme, { top: 0, left: 0, itemGap: px(12, scale), textStyle: { color: T.legend, fontSize: px(10, scale) } }, scale),
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, ...tip(theme, scale) },
    xAxis: {
      type: "category",
      data: data.weeks,
      ...axisX(theme, scale),
      axisLabel: {
        color: T.label,
        fontSize: px(10, scale),
        interval: 0,
        formatter: (value: string, index: number) => (index === last ? `${value}*` : value)
      }
    },
    yAxis: { type: "value", ...axisY(theme, scale) },
    series: data.series.map((s, i) => {
      const isAtRisk = s.name === "At risk";
      const isTop = i === data.series.length - 1;
      return {
        name: s.name,
        type: "bar" as const,
        stack: "o",
        data: s.values.map((value, index) => ({
          value,
          itemStyle: {
            color: overdueByWeekColor(s.name, i),
            opacity: !isAtRisk && index === last ? 0.72 : 1,
            borderRadius: isTop ? [3, 3, 0, 0] : 0
          }
        })),
        barMaxWidth: 20
      };
    })
  };
}

export function loadBreakdownOption(theme: ChartTheme, data: LoadBreakdownData, scale = 1): EChartsOption {
  const labels = [...data.labels].reverse();
  const open = [...data.open].reverse();
  const blocked = [...data.blocked].reverse();
  const overdue = [...data.overdue].reverse();
  return {
    ...base(theme, scale),
    grid: { left: px(128, scale), right: 12, top: px(28, scale), bottom: px(22, scale) },
    legend: legendCfg(theme, { top: 0, left: 0 }, scale),
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, ...tip(theme, scale) },
    xAxis: { type: "value", ...axisY(theme, scale) },
    yAxis: {
      type: "category",
      data: labels,
      ...axisX(theme, scale),
      axisLabel: { color: THEMES[theme].label, fontSize: px(10, scale), width: px(120, scale), overflow: "truncate" }
    },
    series: [
      { name: "Open", type: "bar", data: open, barMaxWidth: 10, itemStyle: { color: PALETTE.blue, borderRadius: [0, 3, 3, 0] } },
      { name: "Blocked", type: "bar", data: blocked, barMaxWidth: 10, itemStyle: { color: PALETTE.purple, borderRadius: [0, 3, 3, 0] } },
      { name: "Overdue", type: "bar", data: overdue, barMaxWidth: 10, itemStyle: { color: PALETTE.red, borderRadius: [0, 3, 3, 0] } }
    ]
  };
}

export function closureRateOption(theme: ChartTheme, data: ClosureRateData, scale = 1): EChartsOption {
  const T = THEMES[theme];
  const labels = [...data.labels].reverse();
  const rates = [...data.rates].reverse();
  return {
    ...base(theme, scale),
    grid: { left: px(128, scale), right: px(44, scale), top: px(12, scale), bottom: px(22, scale) },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: (v) => `${v}%`, ...tip(theme, scale) },
    xAxis: { type: "value", max: 100, ...axisY(theme, scale) },
    yAxis: {
      type: "category",
      data: labels,
      ...axisX(theme, scale),
      axisLabel: { color: T.label, fontSize: px(10, scale), width: px(120, scale), overflow: "truncate" }
    },
    series: [
      {
        type: "bar",
        data: rates.map((rate) => ({
          value: rate,
          itemStyle: { color: rate < 70 ? PALETTE.orange : rate < 85 ? PALETTE.blue : PALETTE.green, borderRadius: [0, 4, 4, 0] }
        })),
        barMaxWidth: 16,
        label: { show: true, position: "right", color: T.label, fontSize: px(11, scale), formatter: "{c}%" }
      }
    ]
  };
}

export function closedBySizeOption(theme: ChartTheme, data: LoadSizeMixData, scale = 1): EChartsOption {
  const option = loadSizeMixOption(theme, data, scale);
  const T = THEMES[theme];
  return {
    ...option,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      valueFormatter: (value) => `${value} pts/day`,
      ...tip(theme, scale)
    },
    xAxis: {
      type: "value",
      name: "pts/day",
      nameTextStyle: { color: T.label, fontSize: px(10, scale) },
      ...axisY(theme, scale)
    }
  };
}

export function loadSizeMixOption(theme: ChartTheme, data: LoadSizeMixData, scale = 1): EChartsOption {
  const categories = [...data.categories].reverse();
  return {
    ...base(theme, scale),
    grid: { left: px(128, scale), right: 14, top: px(30, scale), bottom: px(22, scale) },
    legend: legendCfg(theme, { top: 0, left: 0, itemGap: px(10, scale) }, scale),
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, ...tip(theme, scale) },
    xAxis: { type: "value", ...axisY(theme, scale) },
    yAxis: {
      type: "category",
      data: categories,
      ...axisX(theme, scale),
      axisLabel: { color: THEMES[theme].label, fontSize: px(10, scale), width: px(120, scale), overflow: "truncate" }
    },
    series: data.series.map((s, i) => ({
      name: s.name,
      type: "bar" as const,
      stack: "s",
      data: [...s.values].reverse(),
      barMaxWidth: 16,
      itemStyle: { color: colorFor(s.name, i) }
    }))
  };
}

export function typeMixOption(theme: ChartTheme, data: TypeMixData, scale = 1): EChartsOption {
  const T = THEMES[theme];
  const total = data.types.reduce((s, t) => s + t.value, 0);
  return {
    ...base(theme, scale),
    tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)", ...tip(theme, scale) },
    legend: legendCfg(theme, { type: "scroll", bottom: 0, left: "center" }, scale),
    series: [
      {
        type: "pie",
        radius: ["46%", "72%"],
        center: ["50%", "44%"],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: T.surface, borderWidth: 2 },
        label: {
          show: true,
          position: "center",
          formatter: `{a|${total}}\n{b|open}`,
          rich: {
            a: { fontSize: px(24, scale), fontWeight: 500, color: T.text, fontFamily: CHART_FONT },
            b: { fontSize: px(11, scale), color: T.label, fontFamily: CHART_FONT, lineHeight: px(16, scale) }
          }
        },
        labelLine: { show: false },
        emphasis: { label: { show: true }, scaleSize: 4 },
        data: data.types.map((t, i) => ({ name: t.name, value: t.value, itemStyle: { color: colorFor(t.name, i) } }))
      }
    ]
  };
}
